import React, { useState } from "react";
import { db } from "./firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  TextField,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const obtenerFechaLocal = () => {
  const fecha = new Date();
  const y = fecha.getFullYear();
  const m = (fecha.getMonth() + 1).toString().padStart(2, "0");
  const d = fecha.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const ReporteVentasCreditoDetalladoRango = () => {
  const [fechaInicio, setFechaInicio] = useState(obtenerFechaLocal());
  const [fechaFin, setFechaFin] = useState(obtenerFechaLocal());
  const [filas, setFilas] = useState([]); // [{cliente, totalQ, totalCantidad, ventas:[{fecha, totalVenta, items:[{nombre,cantidad,subtotal}]}]}]
  const [totalGeneralQ, setTotalGeneralQ] = useState(0);
  const [totalGeneralCant, setTotalGeneralCant] = useState(0);
  const [abiertos, setAbiertos] = useState({}); // control de expansión por índice

  const toggleOpen = (i) => {
    setAbiertos((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const consultar = async () => {
    try {
      // 1) Traer ventas a crédito en rango
      const ventasRef = collection(db, "ventas");
      const qVentas = query(
        ventasRef,
        where("fecha", ">=", fechaInicio),
        where("fecha", "<=", fechaFin),
        where("tipoVenta", "==", "Crédito")
      );
      const snapVentas = await getDocs(qVentas);

      // Estructuras para agrupar por cliente
      const porCliente = new Map(); // clienteId => { totalQ, totalCantidad, ventas: [] }

      for (const ventaDoc of snapVentas.docs) {
        const venta = ventaDoc.data();
        const clienteId = venta.clienteId || "desconocido";
        const ventaId = ventaDoc.id;
        const fecha = venta.fecha || "";

        // 2) Leer detalles de cada venta y acumular
        const snapDetalles = await getDocs(
          collection(db, `ventas/${ventaId}/detalles`)
        );

        let totalVenta = 0;
        let totalCantVenta = 0;
        const items = [];

        snapDetalles.forEach((det) => {
          const d = det.data();
          const cantidad = parseFloat(d.cantidad) || 0;
          const subtotal = parseFloat(d.subtotal) || 0;
          items.push({
            nombre: d.nombre || "",
            cantidad,
            subtotal,
          });
          totalVenta += subtotal;
          totalCantVenta += cantidad;
        });

        if (!porCliente.has(clienteId)) {
          porCliente.set(clienteId, {
            totalQ: 0,
            totalCantidad: 0,
            ventas: [],
          });
        }
        const nodo = porCliente.get(clienteId);
        nodo.totalQ += totalVenta;
        nodo.totalCantidad += totalCantVenta;
        nodo.ventas.push({ fecha, totalVenta, items });
      }

      // 3) Resolver nombres de clientes
      const resultados = [];
      for (const [clienteId, data] of porCliente) {
        let nombreCliente = "Cliente desconocido";
        try {
          if (clienteId !== "desconocido") {
            const cliSnap = await getDoc(doc(db, "clientes", clienteId));
            if (cliSnap.exists()) {
              nombreCliente = cliSnap.data().nombre || nombreCliente;
            }
          }
        } catch (_) {
          // ignorar errores individuales de lectura de cliente
        }
        resultados.push({
          cliente: nombreCliente,
          totalQ: data.totalQ,
          totalCantidad: data.totalCantidad,
          ventas: data.ventas.sort((a, b) => a.fecha.localeCompare(b.fecha)),
        });
      }

      // 4) Ordenar por total (desc) o alfabético si prefieres
      resultados.sort((a, b) => b.totalQ - a.totalQ);

      // 5) Totales generales
      const tgQ = resultados.reduce((s, r) => s + r.totalQ, 0);
      const tgC = resultados.reduce((s, r) => s + r.totalCantidad, 0);

      setFilas(resultados);
      setTotalGeneralQ(tgQ);
      setTotalGeneralCant(tgC);
      setAbiertos({}); // colapsar todo en nueva consulta
    } catch (err) {
      console.error("Error consultando ventas a crédito:", err);
      setFilas([]);
      setTotalGeneralQ(0);
      setTotalGeneralCant(0);
    }
  };

  return (
    <Box
      sx={{
        mt: 4,
        p: 3,
        backgroundColor: "white",
        borderRadius: 2,
        boxShadow: 3,
        maxWidth: 1000,
        mx: "auto",
        width: "100%",
      }}
    >
      <h2>Ventas a Crédito por Rango (Resumen + Detalle)</h2>

      <TextField
        label="Fecha inicio"
        type="date"
        value={fechaInicio}
        onChange={(e) => setFechaInicio(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Fecha fin"
        type="date"
        value={fechaFin}
        onChange={(e) => setFechaFin(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
      />

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={consultar}
        sx={{ mt: 2 }}
      >
        Consultar
      </Button>

      <Table sx={{ mt: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell><strong>Cliente</strong></TableCell>
            <TableCell align="right"><strong>Cant. total</strong></TableCell>
            <TableCell align="right"><strong>Total (Q)</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                Sin datos para el rango seleccionado
              </TableCell>
            </TableRow>
          ) : (
            filas.map((fila, i) => (
              <React.Fragment key={i}>
                <TableRow>
                  <TableCell width={48}>
                    <IconButton size="small" onClick={() => toggleOpen(i)}>
                      {abiertos[i] ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{fila.cliente}</TableCell>
                  <TableCell align="right">{fila.totalCantidad}</TableCell>
                  <TableCell align="right">{fila.totalQ.toFixed(2)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                    <Collapse in={abiertos[i]} timeout="auto" unmountOnExit>
                      <Box sx={{ m: 2 }}>
                        <strong>Detalle por venta</strong>
                        <Table size="small" sx={{ mt: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Fecha</TableCell>
                              <TableCell>Ítems</TableCell>
                              <TableCell align="right">Cant. venta</TableCell>
                              <TableCell align="right">Total venta (Q)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fila.ventas.map((v, idx) => {
                              const cantVenta = v.items.reduce(
                                (s, it) => s + (parseFloat(it.cantidad) || 0),
                                0
                              );
                              return (
                                <TableRow key={idx}>
                                  <TableCell>{v.fecha}</TableCell>
                                  <TableCell>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                      {v.items.map((it, k) => (
                                        <li key={k}>
                                          {it.nombre} — cant: {it.cantidad} — Q{(it.subtotal || 0).toFixed(2)}
                                        </li>
                                      ))}
                                    </ul>
                                  </TableCell>
                                  <TableCell align="right">{cantVenta}</TableCell>
                                  <TableCell align="right">{(v.totalVenta || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>

      <Box sx={{ mt: 2 }}>
        <h3>
          Totales del rango — Cantidad: {totalGeneralCant} | Total (Q):{" "}
          {totalGeneralQ.toFixed(2)}
        </h3>
      </Box>
    </Box>
  );
};

export default ReporteVentasCreditoDetalladoRango;
